import { emitWithAck } from './emit';
import { SocketDisconnectedError } from './errors';
import { socketLog } from './log';

const DEFAULT_MAX_ATTEMPTS  = 5;
const DEFAULT_BASE_DELAY_MS = 500;
const DEFAULT_MAX_DELAY_MS  = 30_000;
const DEFAULT_TIMEOUT_MS    = 10_000;
// How long to wait for the socket to reconnect before giving up. Keep
// this generous — the saga layer is happy to wait, the user already sees
// a "reconnecting" indicator from the SocketStatus component.
const DEFAULT_WAIT_FOR_CONNECT_MS = 60_000;

function backoffDelay(attempt, baseMs, maxMs) {
    const exp = Math.min(maxMs, baseMs * (2 ** attempt));
    // Symmetrical jitter ±25% so concurrent retries don't stampede.
    const jitter = exp * 0.25 * (Math.random() * 2 - 1);
    return Math.max(0, exp + jitter);
}

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

// Resolves once the socket fires 'connect' (or immediately if already
// connected). Rejects with SocketDisconnectedError if it doesn't reconnect
// within timeoutMs.
function waitForConnection(socket, timeoutMs) {
    if (socket.connected) return Promise.resolve();
    return new Promise((resolve, reject) => {
        let settled = false;
        const onConnect = () => {
            if (settled) return;
            settled = true;
            socket.off('connect', onConnect);
            clearTimeout(timer);
            resolve();
        };
        const timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            socket.off('connect', onConnect);
            reject(new SocketDisconnectedError());
        }, timeoutMs);
        socket.on('connect', onConnect);
    });
}

// emitWithRetry — emitWithAck with reconnect-aware retry.
//
// On `SocketDisconnectedError`: waits for the socket to reconnect (up to
// `waitForConnectMs`) before retrying. Used to bridge transient drops.
//
// On any retriable error (`err.retriable === true`): exponential backoff
// retry up to `maxAttempts` total tries.
//
// On non-retriable errors (auth, server validation, etc.): rethrows
// immediately. Saga layer decides what to do.
//
// Retries are gated on `idempotent: true` (default). Non-idempotent
// requests should pass `idempotent: false` so a network blip doesn't
// double-create.
export async function emitWithRetry(socket, event, data, options = {}) {
    const {
        timeoutMs         = DEFAULT_TIMEOUT_MS,
        maxAttempts       = DEFAULT_MAX_ATTEMPTS,
        baseDelayMs       = DEFAULT_BASE_DELAY_MS,
        maxDelayMs        = DEFAULT_MAX_DELAY_MS,
        waitForConnectMs  = DEFAULT_WAIT_FOR_CONNECT_MS,
        idempotent        = true,
    } = options;

    let lastError = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            return await emitWithAck(socket, event, data, timeoutMs);
        } catch (err) {
            lastError = err;

            // Disconnect-class errors are bridged by waiting for reconnect
            // before counting against the retry budget.
            if (err instanceof SocketDisconnectedError && idempotent) {
                socketLog('warn', 'emit_wait_reconnect', { event, attempt });
                try {
                    await waitForConnection(socket, waitForConnectMs);
                    // Don't increment delay — disconnect was the issue,
                    // not the server. Loop to retry immediately.
                    continue;
                } catch (e) {
                    // Reconnect timed out. Surface the original disconnect
                    // error to the caller.
                    throw err;
                }
            }

            // Non-retriable errors propagate immediately.
            if (!err.retriable || !idempotent) {
                throw err;
            }

            // Last attempt — no point sleeping.
            if (attempt === maxAttempts - 1) break;

            const delay = backoffDelay(attempt, baseDelayMs, maxDelayMs);
            socketLog('warn', 'emit_retry', {
                event,
                attempt: attempt + 1,
                code: err.code,
                delayMs: Math.round(delay),
            });
            await sleep(delay);
        }
    }

    throw lastError;
}
